// ---------------------------------------------------------------
// Per-car share codes. Every car has its own code so the OWNER decides which
// car to hand to a broker. A broker adds a car to their fleet by entering (or
// scanning the QR of) that code. The code is stored on the device so it's
// stable across devices/sessions.
// ---------------------------------------------------------------
import { getAllDevices, updateDevice, getJwt } from './traccar';
import { API_BASE } from './config';

// deterministic 6-char code from the device id + hardware id
function genCode(d) {
  const base = `${d.id}-${d.uniqueId || ''}`;
  let h = 2166136261;
  for (let i = 0; i < base.length; i++) { h ^= base.charCodeAt(i); h = Math.imul(h, 16777619) >>> 0; }
  return 'TA-' + h.toString(36).toUpperCase().padStart(7, '0').slice(0, 7);
}

export function codeForDevice(d) {
  return ((d.attributes || {}).shareCode) || genCode(d);
}

// persist the code onto the device the first time it's shown
export async function ensureDeviceCode(d) {
  const a = d.attributes || {};
  if (a.shareCode) return a.shareCode;
  const code = genCode(d);
  try { await updateDevice({ ...d, attributes: { ...a, shareCode: code } }); } catch { /* ignore */ }
  return code;
}

export const normCode = (c) => String(c || '').trim().toUpperCase().replace(/\s+/g, '');

// resolve a code to a device across ALL devices (broker isn't scoped to it yet)
export async function findDeviceByCode(code) {
  const want = normCode(code);
  if (!want) return null;
  const all = await getAllDevices();
  return all.find((d) => normCode(codeForDevice(d)) === want) || null;
}

// Ask for a car by its code (family / broker / a customer adding a family car). The SERVER finds the car and
// files a pending request for the owner — nobody's app reads every car. → { status: 'requested' | 'pending' |
// 'approved', id, name }; throws with the server's message ("No car found with that code…").
const authJson = () => ({ 'Content-Type': 'application/json', ...(getJwt() ? { Authorization: `Bearer ${getJwt()}` } : {}) });
export async function requestShare(code) {
  const r = await fetch(`${API_BASE}/share/request`, { method: 'POST', credentials: 'include', headers: authJson(), body: JSON.stringify({ code: normCode(code) }) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) { const e = new Error(j.error || 'Could not send the request.'); e.notFound = r.status === 404; throw e; }
  return j;
}
// take your own request / access off a car
export async function leaveShare(deviceId) {
  const r = await fetch(`${API_BASE}/share/leave`, { method: 'POST', credentials: 'include', headers: authJson(), body: JSON.stringify({ deviceId }) });
  if (!r.ok) throw new Error('Could not update.');
}

// URL a QR encodes — opens the broker portal and pre-fills the add-car code
export const brokerAddUrl = (code) => `${window.location.origin}/broker?tagalong&add=${encodeURIComponent(code)}`;
// URL a family QR encodes — opens the family portal and pre-fills the add-car code
export const familyAddUrl = (code) => `${window.location.origin}/family?tagalong&add=${encodeURIComponent(code)}`;
// free QR image (no dependency); encodes the broker add URL
export const qrUrlFor = (code, size = 150) => `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&qzone=1&data=${encodeURIComponent(brokerAddUrl(code))}`;
// free QR image encoding the family add URL
export const qrUrlForFamily = (code, size = 150) => `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&qzone=1&data=${encodeURIComponent(familyAddUrl(code))}`;
